/**
 * ResourceModal Component
 * 
 * A reusable modal component for managing external resources (links, documents, etc.) across the application.
 * Provides a consistent interface for adding, editing, and deleting resources with URL references.
 * 
 * Features:
 * - Add new resources with name and URL
 * - Edit existing resource details
 * - Delete resources with confirmation
 * - Form validation and error display
 * - Loading state management during operations
 * - Responsive design with mobile considerations
 * 
 * Usage in Project:
 * 1. Places Module (/src/PlaceDetails.js):
 *    - Managing external resources for places of interest
 *    - Integration with map links, Wikipedia, and Wikitravel
 *    - Custom resource management with edit/delete capabilities
 *    - Section identifier: "places"
 * 
 * Resource Data Structure:
 * {
 *   id: "resource-id",        // Unique identifier
 *   name: "Resource Name",    // Display name
 *   url: "https://...",      // Resource URL
 *   section: "places"        // Section identifier
 * }
 * 
 * Integration Points:
 * - Parent components manage open/close state via isOpen prop
 * - Resource data passed via resource prop for edit mode
 * - Section identifier helps categorize resources
 * - Validation errors displayed from parent validation logic
 * - Save/Delete operations delegated to parent handlers
 * 
 * State Management:
 * - Local form state for name and URL
 * - Loading state during async operations
 * - Form reset on modal open/close
 * - Error display handling
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSave - Callback to save resource changes, receives { name, url, section }
 * @param {Function} props.onDelete - Callback to delete a resource, receives resource.id
 * @param {Object} [props.resource=null] - Resource data for edit mode
 *                                        If null, modal opens in create mode
 * @param {string} [props.section=''] - Section identifier where the resource belongs
 *                                     Used for categorizing resources
 * @param {string} [props.validationError=null] - Error message to display
 *                                               Supports multiline errors via \n
 */

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import './ResourceModal.css';

const ResourceModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  resource = null,
  section = '',
  validationError = null,
}) => {
  /**
     * Component State Management
     * 
     * Manages three key pieces of state:
     * 1. name: The resource name input value
     * 2. url: The resource URL input value
     * 3. saving: Loading state during save/delete operations
     * 
     * State is initialized based on whether we're in edit mode (resource prop exists)
     * or create mode (resource is null)
     */
  const [name, setName] = useState(resource ? resource.name : '');
  const [url, setUrl] = useState(resource ? resource.url : '');
  const [saving, setSaving] = useState(false);

  /**
     * Modal Reset Effect
     * 
     * Resets form state when the modal opens or resource changes
     * Ensures form is in correct state for editing or creating new resource
     * 
     * Triggers:
     * - When modal opens/closes (isOpen changes)
     * - When editing different resource (resource changes)
     */
  useEffect(() => {
    if (isOpen) {
      setName(resource ? resource.name : '');
      setUrl(resource ? resource.url : '');
      setSaving(false);
    }
  }, [isOpen, resource]);

  /**
     * Form Submission Handler
     * 
     * Processes the form submission for both create and edit operations
     * Manages loading state and error handling
     * 
     * Flow:
     * 1. Prevent default form submission
     * 2. Set loading state
     * 3. Call parent save handler with form data
     * 4. Close modal on success
     * 5. Handle errors and reset loading state
     * 
     * @param {Event} e - Form submission event
     */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await onSave({ name, url, section });
      onClose();
    } catch (err) {
      console.error('Error in resource operation:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
     * Resource Deletion Handler
     * 
     * Handles the deletion of existing resources
     * Manages loading state and error handling
     * 
     * Flow:
     * 1. Set loading state
     * 2. Call parent delete handler with resource ID
     * 3. Close modal on success
     * 4. Handle errors and reset loading state
     */
  const confirmDelete = async () => {
    setSaving(true);
    try {
      await onDelete(resource.id);
      onClose();
    } catch (err) {
      console.error('Error deleting resource:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={`${resource ? 'Edit' : 'Add'} Resource`}
      className="modal"
    >
      <div className="modal-content">
        {/* Modal Header */}
        <h2>{resource ? 'Edit Resource' : 'Add Resource'}</h2>

        {/* Validation Error Display
                 * Renders validation errors with line break support
                 * Each line of the error message is rendered as a separate div
                 */}
        {validationError && (
          <div className="error-message">
            {validationError.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Resource Name Input Field
                     * Required field for resource identification
                     * Disabled during save/delete operations
                     */}
          <div className="form-group">
            <label htmlFor="resourceName">Resource Name</label>
            <input
              id="resourceName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter resource name"
              disabled={saving}
            />
          </div>

          {/* Resource URL Input Field
                     * Required field for resource location
                     * Disabled during save/delete operations
                     * Includes https:// placeholder for user guidance
                     */}
          <div className="form-group">
            <label htmlFor="resourceUrl">URL</label>
            <input
              id="resourceUrl"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              disabled={saving}
            />
          </div>

          {/* Action Buttons
                     * Primary: Submit form (Add/Save)
                     * Secondary: Cancel operation
                     * Danger (Edit mode only): Delete resource
                     * All buttons disabled during save/delete operations
                     */}
          <div className="modal-buttons">
            <button 
              type="submit" 
              className="primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : (resource ? 'Save Changes' : 'Add Resource')}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              className="secondary"
              disabled={saving}
            >
                            Cancel
            </button>
            {resource && (
              <button
                type="button"
                onClick={confirmDelete}
                className="danger"
                disabled={saving}
              >
                                Delete Resource
              </button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ResourceModal;